import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Camera, TrendingUp, KeyRound, Sparkles, BedDouble, Headphones, Wrench, ShieldCheck, CircleCheck } from "lucide-react";

const Icons = { Camera, TrendingUp, KeyRound, Sparkles, BedDouble, Headphones, Wrench, ShieldCheck };
import { Star, Quote, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import { SectionTitle, CtaLink } from "./Common";
import { STEPS, SERVICES, STATS, ZONES, IMG, PLATFORMS, slugify } from "../../mock";

export function HowItWorks() {
  return (
    <section className="py-24 lg:py-32" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTitle center kicker="Comment ça marche" title="Louer sans contrainte, en 3 étapes" text="Confiez-nous vos clés, on s'occupe du reste. Vous gardez le contrôle total de votre calendrier." />
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 120} className="group relative rounded-[32px] bg-[#f3ede1] p-8 lg:p-10 hover:bg-[#13261c] transition-colors duration-500">
              <span className="font-display text-7xl font-semibold text-[#c4703f]/20 group-hover:text-white/25 transition-colors">{s.n}</span>
              <h3 className="mt-6 text-2xl font-bold text-[#13261c] group-hover:text-white transition-colors">{s.title}</h3>
              <p className="mt-3 text-[#5a6158] leading-relaxed group-hover:text-[#faf7f0] transition-colors">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <section className="py-24 lg:py-32 bg-[#efe7d6]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-end">
          <SectionTitle kicker="Nos services" title="Un service complet, du premier clic au départ" />
          <Reveal delay={100} className="lg:justify-self-end"><CtaLink to="/tarifs" variant="outline" testid="services-tarifs-link">Voir nos formules</CtaLink></Reveal>
        </div>
        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          <Reveal className="lg:row-span-2 relative rounded-[32px] overflow-hidden min-h-[420px]">
            <img loading="lazy" decoding="async" src={IMG.living} alt="Salon lumineux d'un logement Ouest Conciergerie" className="absolute inset-0 w-full h-full object-cover object-[62%_center] hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#13261c]/85 via-[#13261c]/10 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 p-8 text-white pointer-events-none">
              <p className="font-display text-3xl font-semibold leading-tight">Un logement prêt à accueillir, toujours impeccable.</p>
              <p className="mt-3 text-[#faf7f0]/90">Check-list de 60 points après chaque séjour.</p>
            </div>
          </Reveal>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => {
              const I = Icons[s.icon];
              return (
                <Reveal key={s.title} delay={(i % 2) * 100} className="group bg-white rounded-[28px] p-7 ring-1 ring-black/[0.04] hover:-translate-y-1 hover:shadow-[0_30px_50px_-30px_rgba(19,38,28,0.4)] transition-[transform,box-shadow] duration-300">
                  <span className="w-12 h-12 rounded-2xl bg-[#f1ebdd] grid place-items-center group-hover:bg-[#13261c] transition-colors"><I className="w-6 h-6 text-[#c4703f] group-hover:text-white transition-colors" /></span>
                  <h3 className="mt-5 text-lg font-bold text-[#13261c]">{s.title}</h3>
                  <p className="mt-2 text-[15px] text-[#5a6158] leading-relaxed">{s.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function StatsBand() {
  return (
    <section className="relative bg-[#1c3426] grain overflow-hidden" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-2 lg:grid-cols-4 gap-10 relative">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 100} className="text-center lg:text-left">
            <p className="font-display text-5xl lg:text-6xl font-semibold text-white">{s.value}</p>
            <p className="mt-2 text-[#faf7f0]/85 font-medium">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Platforms() {
  return (
    <section className="py-24 lg:py-32 overflow-hidden" data-testid="platforms-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionTitle kicker="Diffusion" title="Votre logement visible là où les voyageurs réservent" text="Nous publions et synchronisons votre annonce sur les plateformes leaders. Un seul calendrier, zéro double réservation, et des prix ajustés partout en même temps." />
          <Reveal delay={150} className="mt-8 space-y-4">
            {["Annonce rédigée en français et en anglais", "Calendriers synchronisés en temps réel", "Paiement directement sur votre compte, 24h après l'arrivée du voyageur"].map((t) => (
              <div key={t} className="flex gap-3 items-start"><CircleCheck className="w-6 h-6 text-[#c4703f] shrink-0" /><p className="text-[#2c3a31] font-medium">{t}</p></div>
            ))}
          </Reveal>
          <Reveal delay={220} className="mt-10"><CtaLink to="/rendez-vous" testid="platforms-cta">Prendre rendez-vous</CtaLink></Reveal>
        </div>
        <Reveal delay={100} className="relative">
          <div className="absolute -inset-8 bg-gradient-to-br from-[#e6dcc8] to-[#efe7d6] rounded-[48px] -rotate-3" />
          <div className="relative grid grid-cols-2 gap-4">
            {PLATFORMS.map((p, i) => (
              <div key={p.name} className={`bg-white rounded-[28px] h-40 grid place-items-center p-8 shadow-[0_30px_60px_-35px_rgba(19,38,28,0.45)] hover:-translate-y-1 transition-transform duration-300 ${i % 2 ? "translate-y-6" : ""}`} data-testid={`platform-card-${i}`}>
                <img loading="lazy" decoding="async" src={p.logo} alt={p.name} className="max-h-14 max-w-full object-contain" />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function PlatformsBand() {
  return (
    <section className="py-10 bg-white border-y border-[#e6dcc8]" data-testid="platforms-band">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center gap-8 md:gap-12">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#6d7268] shrink-0">Votre annonce diffusée sur</p>
        <div className="flex flex-wrap justify-center md:justify-between items-center gap-x-12 gap-y-6 flex-1">
          {PLATFORMS.map((p) => <img key={p.name} src={p.logo} alt={p.name} className={`${p.h} w-auto object-contain opacity-90 hover:opacity-100 transition-opacity`} />)}
        </div>
      </div>
    </section>
  );
}

export function Zones() {
  return (
    <section className="py-24 lg:py-32 bg-[#f3ede1]" data-testid="zones-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTitle kicker="Nos zones" title="De Saint-Cast au Mont-Saint-Michel" text="Une équipe 100 % locale qui vit sur le secteur et intervient sur toute la Côte d'Émeraude et la baie du Mont-Saint-Michel." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ZONES.map((z, i) => (
            <Reveal key={z.name} delay={i * 90}>
              <Link to={`/villes/${slugify(z.name.split(" &")[0])}`} data-testid={`zone-${i}`} className="group block relative rounded-[28px] overflow-hidden aspect-[3/4]">
                <img loading="lazy" decoding="async" src={z.img} alt={z.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#13261c]/90 via-[#13261c]/10 to-transparent" />
                <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity z-10"><ArrowUpRight className="w-5 h-5 text-[#13261c]" /></span>
                <div className="absolute bottom-0 p-6 text-white">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#d9895a]">{z.count} logements</p>
                  <p className="font-display text-2xl font-semibold mt-1">{z.name}</p>
                  <p className="text-sm text-[#faf7f0]/85 mt-1">{z.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const [i, setI] = useState(0);
  const { data: list = [] } = useQuery({ queryKey: ["results"], queryFn: api.results, staleTime: 300_000 });
  if (!list.length) return null;
  const r = list[i % list.length];
  const t = { name: r.author, city: [r.property_type, r.city].filter(Boolean).join(", "), text: r.quote, rating: r.rating };
  const go = (d) => setI((p) => (p + d + list.length) % list.length);
  return (
    <section className="py-24 lg:py-32 bg-[#13261c] relative grain overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-5">
          <SectionTitle light kicker="Ils nous font confiance" title="Des propriétaires sereins" text="Ce que disent les propriétaires qui nous confient leur logement, de Saint-Cast au Mont-Saint-Michel." />
          <div className={`mt-8 gap-3 ${list.length > 1 ? "flex" : "hidden"}`}>
            <button onClick={() => go(-1)} data-testid="testimonial-prev" className="w-12 h-12 rounded-full ring-1 ring-white/25 text-white grid place-items-center hover:bg-white hover:text-[#13261c] transition-colors" aria-label="Précédent"><ChevronLeft /></button>
            <button onClick={() => go(1)} data-testid="testimonial-next" className="w-12 h-12 rounded-full bg-[#d9895a] text-[#13261c] grid place-items-center hover:bg-[#e39d72] transition-colors" aria-label="Suivant"><ChevronRight /></button>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div key={i} className="relative bg-white rounded-[36px] p-8 lg:p-12 animate-in fade-in slide-in-from-right-6 duration-500" data-testid="testimonial-card">
            <Quote className="w-12 h-12 text-[#faf7f0]" />
            <div className="flex mt-4">{[0, 1, 2, 3, 4].map((k) => <Star key={k} className={`w-5 h-5 ${k < t.rating ? "fill-[#d9895a] text-[#d9895a]" : "text-[#faf7f0]"}`} />)}</div>
            <p className="mt-5 font-display text-2xl lg:text-[30px] leading-snug text-[#13261c]">« {t.text} »</p>
            {(r.before || r.after) && (
              <div className="mt-6 flex flex-wrap items-center gap-2 text-sm" data-testid="testimonial-result">
                {r.before && <span className="rounded-full bg-[#f1ebdd] px-3 py-1.5 text-[#5a6158]">Avant : {r.before}</span>}
                {r.after && <span className="rounded-full bg-[#d9895a] px-3 py-1.5 font-bold text-[#13261c]">Avec nous : {r.after}</span>}
              </div>
            )}
            <div className="mt-8 flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-[#13261c] text-white grid place-items-center font-bold">{t.name[0]}</span>
              <div><p className="font-bold text-[#13261c]">{t.name}</p><p className="text-sm text-[#6d7268]">{t.city}</p></div>
            </div>
            <div className="absolute bottom-8 right-8 flex gap-1.5">{list.length > 1 && list.map((_, k) => <span key={k} className={`h-2 rounded-full transition-[width] ${k === i ? "w-6 bg-[#13261c]" : "w-2 bg-[#e6dcc8]"}`} />)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="px-5 lg:px-8 pb-24" data-testid="final-cta">
      <Reveal className="max-w-7xl mx-auto relative rounded-[40px] overflow-hidden bg-gradient-to-br from-[#13261c] to-[#24402f] px-8 py-16 lg:p-20">
        <img loading="lazy" decoding="async" src={IMG.keys} alt="" className="hidden lg:block absolute right-0 top-0 h-full w-[40%] object-cover" />
        <div className="hidden lg:block absolute right-[40%] top-0 h-full w-40 bg-gradient-to-r from-[#1c3426] to-transparent" />
        <div className="relative lg:max-w-[55%]">
          <h2 className="font-display text-4xl lg:text-[56px] font-semibold text-white leading-[1.05]">Et si votre logement travaillait pour vous ?</h2>
          <p className="mt-5 text-lg text-[#faf7f0]/90">Simulation gratuite et sans engagement en moins de 2 minutes, ou prenez directement rendez-vous avec notre équipe locale.</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <CtaLink to="/proprietaires" variant="yellow" testid="final-cta-estimate">Découvrir mon potentiel</CtaLink>
            <Link to="/rendez-vous" data-testid="final-cta-rdv" className="inline-flex items-center rounded-full px-7 py-4 font-bold text-white ring-1 ring-white/40 hover:bg-white/10 transition-colors">Prendre rendez-vous</Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
